import { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../context/AuthContext';
import './ClientHistory.css';

const ClientHistoryDisplay = ({ selectedClient }) => {

    //state to control modal visibility
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [notes, setNotes] = useState([]);
    const [newNote, setNewNote] = useState('');
    const [editingNote, setEditingNote] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    
    // Preview notes state
    const [previewNotes, setPreviewNotes] = useState([]);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState('');

    // Helper to truncate note content for preview (mobile only)
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
      const checkMobile = () => setIsMobile(window.matchMedia('(max-width: 600px)').matches);
      checkMobile();
      window.addEventListener('resize', checkMobile);
      return () => window.removeEventListener('resize', checkMobile);
    }, []);
    const truncate = (str, max = 40) => {
      if (!str) return '';
      return str.length > max ? str.slice(0, max) + '…' : str;
    };
    
    const { session } = useAuth();

    // Cleanup effect to remove modal-open class on unmount
    useEffect(() => {
        return () => {
            document.body.classList.remove('modal-open');
        };
    }, []);

    // Fetch notes from database
    const fetchNotes = async () => {
        if (!selectedClient) return;
        
        setLoading(true);
        setError('');
        
        try {
            // First, get all notes for this client
            const { data: notesData, error: notesError } = await supabase
                .from('client_notes')
                .select('*')
                .eq('client_id', selectedClient.id)
                .order('created_at', { ascending: false });
            
            if (notesError) throw notesError;
            
            if (!notesData || notesData.length === 0) {
                setNotes([]);
                return;
            }
            
            // Get unique trainer IDs from the notes
            const trainerIds = [...new Set(notesData.map(note => note.trainer_id))];
            
            // Get trainer names from profiles table
            const { data: profilesData, error: profilesError } = await supabase
                .from('profiles')
                .select('id, display_name')
                .in('id', trainerIds);
            
            if (profilesError) {
                console.warn('Error fetching trainer profiles:', profilesError);
                // Still show notes even if we can't get trainer names
                setNotes(notesData);
                return;
            }
            
            // Combine notes with trainer names
            const notesWithTrainerNames = notesData.map(note => ({
                ...note,
                trainer_name: profilesData?.find(profile => profile.id === note.trainer_id)?.display_name
            }));
            
            setNotes(notesWithTrainerNames);
        } catch (err) {
            setError('Error fetching notes: ' + err.message);
            console.error('Error fetching notes:', err);
        } finally {
            setLoading(false);
        }
    };

    // Real-time subscription for notes updates (like client search in Workout.js)
    useEffect(() => {
        if (!selectedClient || !isModalOpen) return;

        // Initial fetch
        fetchNotes();

        // Subscribe to real-time changes on client_notes table
        const subscription = supabase
            .channel('client_notes_changes')
            .on('postgres_changes', 
                { 
                    event: '*', 
                    schema: 'public', 
                    table: 'client_notes',
                    filter: `client_id=eq.${selectedClient.id}`
                }, 
                (payload) => {
                    console.log('Notes change detected:', payload);
                    // Refetch notes when any change occurs for this client
                    fetchNotes();
                }
            )
            .subscribe();

        // Cleanup subscription on unmount or when modal closes
        return () => {
            subscription.unsubscribe();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedClient?.id, isModalOpen]); // Depend on client ID and modal state

    // Save new note
    const saveNote = async () => {
        if (!selectedClient || !session || !newNote.trim()) return;
        
        setLoading(true);
        setError('');
        
        try {
            const { error } = await supabase
                .from('client_notes')
                .insert({
                    client_id: selectedClient.id,
                    trainer_id: session.user.id,
                    note_content: newNote.trim()
                })
                .select()
                .single();
            
            if (error) throw error;
            
            // Clear the form and refresh notes immediately
            setNewNote('');
            // Fetch notes immediately to ensure UI updates
            fetchNotes();
        } catch (err) {
            setError('Error saving note: ' + err.message);
            console.error('Error saving note:', err);
        } finally {
            setLoading(false);
        }
    };

    // Update existing note
    const updateNote = async () => {
        if (!editingNote || !editingNote.note_content.trim()) return;
        
        setLoading(true);
        setError('');
        
        try {
            const { error } = await supabase
                .from('client_notes')
                .update({
                    note_content: editingNote.note_content.trim()
                })
                .eq('id', editingNote.id)
                .select()
                .single();
            
            if (error) throw error;
            
            // Clear editing state and refresh notes immediately
            setEditingNote(null);
            // Fetch notes immediately to ensure UI updates
            fetchNotes();
        } catch (err) {
            setError('Error updating note: ' + err.message);
            console.error('Error updating note:', err);
        } finally {
            setLoading(false);
        }
    };

    // Start editing a note
    const startEditing = (note) => {
        setEditingNote({ ...note });
    };

    // Cancel editing
    const cancelEditing = () => {
        setEditingNote(null);
    };

    // Helper function to determine if current user can edit this note
    const canEditNote = (note) => {
        return note.trainer_id === session?.user?.id;
    };

    // Helper function to get trainer display info
    const getTrainerDisplayInfo = (note) => {
        const isCurrentUser = canEditNote(note);
        if (isCurrentUser) {
            return 'You';
        }
        
        // Use trainer name from profiles table if available
        if (note.trainer_name) {
            return note.trainer_name;
        }
        
        // Fallback to partial trainer ID if profile not found
        return `Trainer (${note.trainer_id.slice(0, 8)}...)`;
    };

    // --- Preview notes fetcher ---
    // Fetch preview notes when client changes
    useEffect(() => {
        if (!selectedClient?.id) return;

        const fetchPreviewNotes = async () => {
            setPreviewLoading(true);
            setPreviewError('');
            try {
                // get the two most recent notes for this client
                const { data: notesData, error: notesError } = await supabase
                    .from('client_notes')
                    .select('*')
                    .eq('client_id', selectedClient.id)
                    .order('created_at', { ascending: false })
                    .limit(2);

                if (notesError) throw notesError;

                if (!notesData || notesData.length === 0) {
                    setPreviewNotes([]);
                    return;
                }

                // fetch trainer display names (optional, mirrors your full fetch)
                const trainerIds = [...new Set(notesData.map(n => n.trainer_id))];
                const { data: profilesData, error: profilesError } = await supabase
                    .from('profiles')
                    .select('id, display_name')
                    .in('id', trainerIds);

                if (profilesError) {
                    console.warn('Error fetching trainer profiles (preview):', profilesError);
                    setPreviewNotes(notesData);
                    return;
                }

                const withNames = notesData.map(note => ({
                    ...note,
                    trainer_name: profilesData?.find(p => p.id === note.trainer_id)?.display_name
                }));

                setPreviewNotes(withNames);
            } catch (err) {
                console.error('Error fetching preview notes:', err);
                setPreviewError('Error fetching recent notes: ' + err.message);
            } finally {
                setPreviewLoading(false);
            }
        };

        fetchPreviewNotes();
    }, [selectedClient?.id]);

    //function to open modal
    const openModal = () => {
        setIsModalOpen(true);
        document.body.classList.add('modal-open');
        // fetchNotes() now handled by real-time subscription useEffect
    };

    //function to close modal
    const closeModal = () => {
        setIsModalOpen(false);
        document.body.classList.remove('modal-open');
        setNewNote('');
        setEditingNote(null);
        setError('');
    };

  // Don't render if no client selected
  if (!selectedClient) {
    return null;
  }

  return (
    <div className='client-history-display'>
      {/* Preview notes section */}
      <div className='notes-preview'>
        <h3>Recent Notes</h3>

        {previewLoading && <div className='loading-message'>Loading…</div>}
        {previewError && <div className='error-message'>{previewError}</div>}

        {!previewLoading && previewNotes.length === 0 && (
          <div className='no-notes-message'>No notes yet.</div>
        )}

        {previewNotes.map(note => (
          <div key={note.id} className='note-item note-item--compact'>
            <div className='note-content note-content--clamped'>
              {isMobile ? truncate(note.note_content, 40) : note.note_content}
            </div>
            <div className='note-metadata'>
              <span className='note-date'>
                {new Date(note.created_at).toLocaleDateString()}
              </span>
              <span className='note-trainer'>By: {getTrainerDisplayInfo(note)}</span>
            </div>
          </div>
        ))}

        {/* Always show the View all notes button so users can add notes */}
        <button type="button" onClick={openModal} className='view-history-button'>
          View all notes
        </button>
      </div>

      {/* Existing modal button and modal code */}

        {isModalOpen && (
            <div className='modal-overlay' onClick={closeModal}>
                <div className='modal-content' onClick={(e) => e.stopPropagation()}>
                    <button type="button" onClick={closeModal} className='modal-close'>×</button>
                    <h2>Notes for {selectedClient.first_name} {selectedClient.last_name}</h2>
                    
                    {error && (
                        <div className='error-message'>
                            {error}
                        </div>
                    )}
                    
                    {/* Add New Note Section */}
                    <div className='add-note-section'>
                        <h3>Add New Note</h3>
                        <textarea
                            value={newNote}
                            onChange={(e) => setNewNote(e.target.value)}
                            placeholder="Enter your note here..."
                            className='note-textarea'
                            rows={4}
                        />
                        <button 
                            type="button" 
                            onClick={saveNote}
                            disabled={!newNote.trim() || loading}
                            className='btn-primary'
                        >
                            {loading ? 'Saving...' : 'Save Note'}
                        </button>
                    </div>
                    
                    {/* Notes List */}
                    <div className='notes-list'>
                        <h3>Previous Notes ({notes.length})</h3>
                        
                        {loading && notes.length === 0 && (
                            <div className='loading-message'>Loading notes...</div>
                        )}
                        
                        {notes.length === 0 && !loading && (
                            <div className='no-notes-message'>No notes yet. Add your first note above!</div>
                        )}
                        
                        {notes.map((note) => (
                            <div key={note.id} className='note-item'>
                                {editingNote && editingNote.id === note.id ? (
                                    // Edit mode
                                    <div className='note-edit-mode'>
                                        <textarea
                                            value={editingNote.note_content}
                                            onChange={(e) => setEditingNote({
                                                ...editingNote,
                                                note_content: e.target.value
                                            })}
                                            className='note-textarea'
                                            rows={3}
                                        />
                                        <div className='note-edit-actions'>
                                            <button 
                                                type="button"
                                                onClick={updateNote}
                                                disabled={loading}
                                                className='btn-primary btn-small'
                                            >
                                                {loading ? 'Saving...' : 'Save'}
                                            </button>
                                            <button 
                                                type="button"
                                                onClick={cancelEditing}
                                                className='btn-secondary btn-small'
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    // View mode
                                    <div className='note-view-mode'>
                                        <div className='note-content'>
                                            {note.note_content}
                                        </div>
                                        <div className='note-metadata'>
                                            <span className='note-date'>
                                                {new Date(note.created_at).toLocaleDateString()} at {new Date(note.created_at).toLocaleTimeString()}
                                            </span>
                                            <span className='note-trainer'>
                                                By: {getTrainerDisplayInfo(note)}
                                            </span>
                                            {note.updated_at !== note.created_at && (
                                                <span className='note-updated'>
                                                    (Updated: {new Date(note.updated_at).toLocaleDateString()})
                                                </span>
                                            )}
                                        </div>
                                        {canEditNote(note) && (
                                            <button 
                                                type="button"
                                                onClick={() => startEditing(note)}
                                                className='btn-edit'
                                            >
                                                Edit
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                    
                    <div className='modal-footer'>
                        <button type="button" onClick={closeModal} className='btn-secondary'>Close</button>
                    </div>
                </div>
            </div>
        )}
    </div>
  )
}

export default ClientHistoryDisplay